import React, { useContext, useState } from "react";
import { AppBar, Toolbar, Typography, CssBaseline, Box, Container, IconButton, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate } from 'react-router-dom';
import CustomButton from '../Button';
import { logout } from "../../services/apiService";
import { LoadingButton } from "@mui/lab";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Menus from "./Menus";
import NotificationBell from "./NotificationBell";
import { colors } from "../../theme/prototypeTokens";

function LogoutPopup({ open, onConfirm, onCancel, loading }) {
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby="logout-dialog-title"
            aria-describedby="logout-dialog-description"
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
            <DialogContent>
                <DialogContentText id="logout-dialog-description">
                    Are you sure you want to log out?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} variant="outlined" disabled={loading} autoFocus>
                    Cancel
                </Button>
                <LoadingButton loading={loading} onClick={onConfirm} variant="contained" color="error">
                    Log out
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

function HeaderV1() {
    const [showLogoutPopup, setShowLogoutPopup] = useState(false);
    const [loading, setLoading] = useState(false);
    const userId = localStorage.getItem('id');
    const refreshToken = localStorage.getItem('token');
    const navigate = useNavigate();

    const handleLogout = async () => {
        setLoading(true);
        try {
            const response = await logout(userId, refreshToken);
            if (response?.data?.code === 200) {
                localStorage.clear();
                navigate('/login');
            }
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutClick = () => {
        if (!userId || !refreshToken) {
            navigate("/login");
        } else {
            setShowLogoutPopup(true);
        }
    };
    const handleProfile = () =>{
        navigate("/myaccount");
    }

    const handleCancelLogout = () => setShowLogoutPopup(false);

    const iconBtnSx = {
        width: 40,
        height: 40,
        color: 'rgba(255,255,255,.85)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,.1)', color: '#fff' },
    };

    const renderAuthButton = () => (
        userId && refreshToken ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, order: { xs: 1, md: 0 } }}>
            <NotificationBell sx={iconBtnSx} />
            <Tooltip title="My Account">
                <IconButton onClick={handleProfile} aria-label="my account" sx={iconBtnSx}>
                    <AccountCircleIcon />
                </IconButton>
            </Tooltip>
            {/* Logout icon stays for desktop/tablet; on mobile it's replaced by a labeled
                "Logout" item at the end of the hamburger drawer menu instead. */}
            <Tooltip title="Logout">
                <IconButton
                    onClick={handleLogoutClick}
                    aria-label="logout"
                    sx={{ ...iconBtnSx, display: { xs: 'none', md: 'inline-flex' } }}
                >
                    <LogoutIcon />
                </IconButton>
            </Tooltip>
            </Box>
        ) : (
            <Button
                variant="outlined"
                color="info"
                onClick={handleLogoutClick}
                sx={{
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    border: '1px solid #ccc',
                    order: { xs: 1, md: 0 },
                }}
            >
                Login
            </Button>
        )
    );

    return (
        <>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: `linear-gradient(180deg, ${colors.navy}, ${colors.navyDark})`,
                    boxShadow: '0 1px 0 rgba(255,255,255,.06) inset, 0 4px 16px rgba(0,0,0,.18)',
                }}
            >
                <Toolbar sx={{ gap: { xs: 1.5, md: 3.5 }, px: { xs: 1.5, md: 3.5 } }}>
                    {/* Logo — full wordmark on tablet/desktop, icon-only on narrow mobile screens so the
                        hamburger menu and auth icons always have room */}
                    <Typography variant="h6" noWrap sx={{ fontSize: '1.05rem', fontWeight: 600, letterSpacing: '-.01em', flexShrink: 0 }}>
                        <Link
                            to="/asset/search"
                            style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}
                        >
                            <img src="/impelsys-logo.png" alt="Impelsys Asset Library" style={{ height: 30, borderRadius: 4 }} />
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Impelsys Asset Library</Box>
                        </Link>
                    </Typography>

                    <Menus onLogoutClick={handleLogoutClick} />

                    {/* Auth Button */}
                    {renderAuthButton()}
                </Toolbar>
            </AppBar>
            <LogoutPopup open={showLogoutPopup} onConfirm={handleLogout} onCancel={handleCancelLogout} loading={loading} />
        </>
    );
}

export default HeaderV1;
